import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface AreaSEOContentProps {
  areaName: string;
  areaSlug: string;
  population: string;
  demographics: {
    medianIncome: string;
    homeownership: string;
    avgHomeValue: string;
  };
}

const AreaSEOContent: React.FC<AreaSEOContentProps> = ({
  areaName,
  areaSlug,
  population,
  demographics
}) => {
  const { language } = useLanguage();

  const localBusinessTypes = language === 'en' ? [
    'sod installation',
    'grass delivery'
  ] : [
    'instalación de césped',
    'entrega de césped'
  ];

  const nearbyAreas = [
    'The Woodlands', 'Cypress', 'Spring', 'Tomball', 
    'Magnolia', 'Conroe', 'Montgomery', 'Willis'
  ].filter(area => area !== areaName);

  return (
    <div className="bg-background">
      {/* Area Information Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">
              {language === 'en' 
                ? `About ${areaName}, Texas`
                : `Acerca de ${areaName}, Texas`
              }
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-xl font-semibold mb-4">
                  {language === 'en' ? 'Community Demographics' : 'Demografía de la Comunidad'}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Population:' : 'Población:'}
                    </span>
                    <span className="font-medium">{population}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Median Income:' : 'Ingreso Promedio:'}
                    </span>
                    <span className="font-medium">{demographics.medianIncome}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Homeownership:' : 'Propietarios:'}
                    </span>
                    <span className="font-medium">{demographics.homeownership}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Avg Home Value:' : 'Valor Promedio Casa:'}
                    </span>
                    <span className="font-medium">{demographics.avgHomeValue}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-4">
                  {language === 'en' ? 'Our Services in' : 'Nuestros Servicios en'} {areaName}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {localBusinessTypes.map((service, index) => (
                    <div key={index} className="text-sm text-muted-foreground capitalize">
                      • {service}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Service Area Coverage */}
            <div className="bg-muted/30 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-semibold mb-4">
                {language === 'en' 
                  ? `Grass & Sod Service Coverage in ${areaName}`
                  : `Cobertura de Servicios de Pasto y Césped en ${areaName}`
                }
              </h3>
              <p className="text-muted-foreground mb-4">
                {language === 'en'
                  ? `The Grass Depot LLC provides professional sod installation and grass delivery throughout ${areaName} and surrounding Montgomery County areas. Our local team understands the soil conditions and climate of ${areaName} to recommend the best sod varieties.`
                  : `The Grass Depot LLC proporciona instalación profesional de césped y entrega de pasto en todo ${areaName} y las áreas circundantes del Condado de Montgomery. Nuestro equipo local entiende el suelo y el clima de ${areaName} para recomendar las mejores variedades de césped.`
                }
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">
                    {language === 'en' ? 'Nearby Service Areas:' : 'Áreas de Servicio Cercanas:'}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {nearbyAreas.slice(0, 4).map((area, index) => (
                      <span key={index} className="bg-primary/10 text-primary px-2 py-1 rounded text-sm">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">
                    {language === 'en' ? 'Service Radius:' : 'Radio de Servicio:'}
                  </h4>
                    <p className="text-sm text-muted-foreground">
                      {language === 'en'
                        ? '30-mile radius from our main location with delivery available throughout Montgomery County for most orders.'
                        : 'Radio de 30 millas desde nuestra ubicación principal con entrega disponible en todo el Condado de Montgomery para la mayoría de pedidos.'
                      }
                    </p>
                </div>
              </div>
            </div>

            {/* FAQ Section for Local SEO */}
            <div>
              <h3 className="text-xl font-semibold mb-6">
                {language === 'en' 
                  ? `Frequently Asked Questions - ${areaName} Grass & Sod`
                  : `Preguntas Frecuentes - Pasto y Césped ${areaName}`
                }
              </h3>
              
              <div className="space-y-4">
                <div className="border-l-4 border-primary pl-4">
                  <h4 className="font-medium mb-2">
                    {language === 'en' 
                      ? `How much does sod installation cost in ${areaName}?`
                      : `¿Cuánto cuesta la instalación de césped en ${areaName}?`
                    }
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {language === 'en'
                      ? `Sod installation costs in ${areaName} vary based on lawn size and access. Typical projects range from $2,500-$8,000+. We\'ll recommend the right variety and provide a clear quote.`
                      : `Los costos de instalación de césped en ${areaName} varían según el tamaño del jardín y el acceso. Los proyectos típicos van de $2,500 a $8,000+. Recomendaremos la variedad adecuada y brindaremos una cotización clara.`
                    }
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h4 className="font-medium mb-2">
                    {language === 'en' 
                      ? `Do you offer grass delivery in ${areaName}?`
                      : `¿Ofrecen entrega de pasto en ${areaName}?`
                    }
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {language === 'en'
                      ? `Yes, we offer grass and sod delivery to ${areaName}. Delivery is available throughout Montgomery County for most orders placed on weekdays.`
                      : `Sí, ofrecemos entrega de pasto y césped a ${areaName}. La entrega está disponible en todo el Condado de Montgomery para la mayoría de los pedidos realizados entre semana.`
                    }
                  </p>
                </div>
                
                <div className="border-l-4 border-primary pl-4">
                  <h4 className="font-medium mb-2">
                    {language === 'en' 
                      ? `What type of grass grows best in ${areaName}?`
                      : `¿Qué tipo de césped crece mejor en ${areaName}?`
                    }
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {language === 'en'
                      ? `St. Augustine and Bermuda grass varieties perform excellently in ${areaName}'s climate. We recommend St. Augustine for shaded areas and Bermuda for full sun locations.`
                      : `Las variedades de césped St. Augustine y Bermuda funcionan excelentemente en el clima de ${areaName}. Recomendamos St. Augustine para áreas sombreadas y Bermuda para ubicaciones de pleno sol.`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden SEO Content */}
      <div className="sr-only">
        <h2>{areaName} Texas Grass and Sod Services</h2>
        <p>Professional sod installer serving {areaName}, Texas residents with premium grass varieties, sod installation, and local delivery. Local {areaName} sod company, Montgomery County grass services, Texas sod installer.</p>
      </div>
    </div>
  );
};

export default AreaSEOContent;